import html2canvas from "html2canvas";
import React from "react";

import { useLocation } from "../../contexts/LocationContext";
import { getCensus, getPeopleByRace, getCensusByRace, getStatesNames } from "../../data";
import BarChart from "../BarChart";
import Card from "../Card";
import SelectLocation from "../SelectLocation";
import styles from "./BarGraphic.module.css";

function stringToDivs(string) {
  return string.split(" ").map((w) => <div>{w}</div>);
}

export function splitPhrase(word) {
  if (typeof word !== "string") return ["", ""];

  const wordArr = word.split(" ");
  const middle = Math.floor(wordArr.length / 2);

  return [wordArr.slice(0, middle).join(" "), wordArr.slice(middle).join(" ")];
}

const getPerMillion = (percent, deaths, location, race) => {
  if (location === "US") {
    const census = getCensus();

    const a = census.total * percent;
    const b = deaths;
    const c = 1000000;
  
    return (c * b) / a;
  } else {
    const censusByRace = getCensusByRace(location);
    const statesNames = getStatesNames()

    const races = {
      Hispanic: "Hispanic",
      White: "WhiteTotal",
      Black: "BlackTotal",
      Asian: "AsianTotal",
      "Native American": "IndianTotal",
      "Native Hawaiian and Pacific Islander": "HawaiianTotal",
    }

    const censusByRaceLocation = censusByRace.find((census) => census.State === statesNames[location])
    const a = censusByRaceLocation[races[race]];
    const b = deaths;
    const c = 1000000;
  
    return (c * b) / a;
  }
};

const getFactor = (location) => {
  const census = getCensus(location);
  let peopleByRace = getPeopleByRace();

  let [blackPeple] = peopleByRace.filter((p) => p.race === "Black");
  let [whitePeple] = peopleByRace.filter((p) => p.race === "White");

  if (location !== "US") {
    blackPeple.data = blackPeple.data.filter((d) => d.state === location);
    whitePeple.data = whitePeple.data.filter((d) => d.state === location);
  }

  const blackPerMillion = getPerMillion(
    census.percents.Black,
    blackPeple.data.length,
    location,
    "Black"
  );

  const whitePerMillion = getPerMillion(
    census.percents.White,
    whitePeple.data.length,
    location,
    "White"
  );

  let factor = 0;
  let label = "";

  if (blackPerMillion > whitePerMillion) {
    factor = blackPerMillion / whitePerMillion;
    label = "more";
  } else {
    factor = whitePerMillion / blackPerMillion;
    label = "less";
  }

  return [factor.toFixed(2), label];
};

function getChartInfo(location) {
  const census = getCensus(location);
  let peopleByRace = getPeopleByRace();

  if (location !== "US") {
    peopleByRace = peopleByRace.map((p) => {
      p.data = p.data.filter((d) => d.state === location);
      return p;
    });
  }

  const peoplePerMillion = peopleByRace.map((p) => {
    return {
      race: p.race,
      count: Math.floor(getPerMillion(census.percents[p.race], p.data.length, location, p.race)),
    };
  });

  peoplePerMillion.sort((a, b) => b.count - a.count);

  const data = peoplePerMillion.map((p) => p.count);
  const races = peoplePerMillion.map((p) => splitPhrase(p.race));

  const categories = races.map((r) => r.filter((i) => i));

  return [data, categories];
}

const BarGraphic = () => {
  const printRef = React.useRef();

  const handleDownloadImage = async () => {
    const element = printRef.current;
    const canvas = await html2canvas(element);

    const data = canvas.toDataURL("image/jpg");
    const link = document.createElement("a");

    if (typeof link.download === "string") {
      link.href = data;
      link.download = "image.jpg";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      window.open(data);
    }
  };

  const downLoadImage = async () => {
    await handleDownloadImage();
  };

  const location = useLocation();
  const [factor, label] = getFactor(location);
  const [data, categories] = getChartInfo(location);

  return (
    <div className={styles.container} ref={printRef}>
      <Card
        downLoadImage={downLoadImage}
        message={`Black people are ${factor} ${label} likely to be killed by the police than white people in ${location}`}
      >
        <div className={styles.content}>
          <div className={styles.text}>
            {stringToDivs("Black people are")}
            <div className={styles.count}>{`${factor}x`}</div>
            <div className={styles.paddingLeft}>{label}</div>
            {stringToDivs(
              "likely to be killed by the police than white people in"
            )}
            <div className={styles.filter}>
              <SelectLocation withLabel={false} small={true} />
            </div>
          </div>
          <div>
            <div className={styles.title}>
              <div className={styles.childBottom}>
                Police killings per 1 million people in the U.S., 2013-2022
              </div>
            </div>
            <div>
              <BarChart data={data} categories={categories} />
            </div>
            <div className={styles.footer}>
              <div className={styles.childTop}>
                Race and ethnicity population data from the 2020 Decennial
                Census
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default BarGraphic;
